import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Section, SectionHeader } from "@/components/site/Section";
import { DimensionBars } from "@/components/site/DimensionBars";
import { GapLists } from "@/components/site/GapLists";
import { relationshipLabels } from "@/content/review";
import type { FullReport } from "@/content/review";
import {
  adminGetCycleReport,
  adminListCycles,
  adminSetUnlocked,
  amIReviewAdmin,
  type AdminCycleRow,
} from "@/utils/review.functions";

export const Route = createFileRoute("/_authenticated/admin/review")({
  head: () => ({
    meta: [
      { title: "360 checks admin | Leadership Edge Live" },
      {
        name: "description",
        content: "Staff view for reading and opening the full 360 report for a leader.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "360 checks admin" },
      { property: "og:description", content: "Staff view for reading full 360 reports." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminReviewPage,
});

function ReportPanel({ cycleId }: { cycleId: string }) {
  const load = useServerFn(adminGetCycleReport);
  const [report, setReport] = useState<FullReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load({ data: { cycleId } })
      .then((result) => setReport(result.report as FullReport))
      .catch(() => setError("We couldn't load this report."));
  }, [load, cycleId]);

  if (error) return <p className="mt-6 text-sm text-muted-foreground">{error}</p>;
  if (!report) return <p className="mt-6 text-sm text-muted-foreground">Loading the report.</p>;

  return (
    <div className="mt-8 space-y-12 border-t border-border/60 pt-8">
      <DimensionBars bars={report.dimensions} />

      <GapLists
        title="Blind spots"
        intro="They rate themselves higher here than the people around them do."
        rows={report.blindSpots}
        emptyLine="No blind spots stand out yet."
      />
      <GapLists
        title="Hidden strengths"
        intro="Others see more of this than they give themselves credit for."
        rows={report.hiddenStrengths}
        emptyLine="Nothing here yet."
      />
      <GapLists
        title="Agreed gaps"
        intro="Both sides land low on these. Start the conversation here."
        rows={report.agreedGaps}
        showCourses
        emptyLine="No behavior is low on both sides."
      />

      <div>
        <h3 className="font-display text-2xl font-semibold text-ink">Group by group</h3>
        <div className="mt-5 grid gap-5 md:grid-cols-3">
          {report.groups.map((group) => {
            const breakdown = report.groupBreakdown.find(
              (b) => b.relationship === group.relationship,
            );
            const average = breakdown?.rows.length
              ? breakdown.rows.reduce((total, row) => total + row.avg, 0) / breakdown.rows.length
              : null;
            return (
              <div
                key={group.relationship}
                className="rounded-2xl border border-border/60 bg-background p-7"
              >
                <h4 className="font-display text-lg font-semibold text-ink">
                  {relationshipLabels[group.relationship]}
                </h4>
                {group.eligible && average !== null ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Average <span className="font-semibold text-ink">{average.toFixed(1)}</span>{" "}
                    from {group.submitted} {group.submitted === 1 ? "response" : "responses"}.
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Waiting on {group.needed} more {group.needed === 1 ? "response" : "responses"}.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-display text-2xl font-semibold text-ink">All 16 behaviors</h3>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[420px] text-left text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wider text-primary">
                <th className="py-2 pr-4">Behavior</th>
                <th className="py-2 pr-4">Self</th>
                <th className="py-2 pr-4">Others</th>
                <th className="py-2">Raters</th>
              </tr>
            </thead>
            <tbody>
              {report.behaviors.map((row) => (
                <tr key={row.slug} className="border-t border-border/60">
                  <td className="py-2 pr-4 text-ink">{row.name}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{row.self?.toFixed(1) ?? "–"}</td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {row.others?.toFixed(1) ?? "–"}
                  </td>
                  <td className="py-2 text-muted-foreground">{row.raters}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminReviewPage() {
  const checkAdmin = useServerFn(amIReviewAdmin);
  const list = useServerFn(adminListCycles);
  const setUnlocked = useServerFn(adminSetUnlocked);

  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [rows, setRows] = useState<AdminCycleRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    list()
      .then(setRows)
      .catch(() => setRows([]));
  }, [list]);

  useEffect(() => {
    checkAdmin()
      .then((result) => {
        setAllowed(result.admin);
        if (result.admin) refresh();
      })
      .catch(() => setAllowed(false));
  }, [checkAdmin, refresh]);

  if (allowed === null) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">Checking your access.</p>
      </Section>
    );
  }

  if (!allowed) {
    return (
      <Section>
        <SectionHeader
          title="This page is for staff"
          intro="Your account doesn't have access to the 360 admin view."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
      </Section>
    );
  }

  return (
    <Section>
      <SectionHeader
        eyebrow="Staff"
        title="360 checks"
        intro="Read a report before the call, then open it for the leader once you have walked them through it."
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
      />

      <div className="space-y-4">
        {rows === null && <p className="text-sm text-muted-foreground">Loading checks.</p>}
        {rows?.length === 0 && <p className="text-sm text-muted-foreground">No checks yet.</p>}
        {rows?.map((row) => (
          <div key={row.id} className="rounded-2xl border border-border/60 bg-background p-7">
            <div className="md:flex md:items-center md:justify-between md:gap-6">
              <div>
                <h3 className="font-display text-lg font-semibold text-ink">
                  {row.leaderName || "Unnamed check"}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">{row.id}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {row.status} ·{" "}
                  {(["direct_report", "peer", "manager"] as const)
                    .map((rel) => `${relationshipLabels[rel]} ${row.submitted[rel]}`)
                    .join(" · ")}
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 md:mt-0">
                <button
                  type="button"
                  onClick={() => setOpenId(openId === row.id ? null : row.id)}
                  className="rounded-[6px] border border-primary px-6 py-3 text-sm font-semibold text-primary"
                >
                  {openId === row.id ? "Hide report" : "Read the report"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    setUnlocked({ data: { cycleId: row.id, unlocked: !row.unlocked } })
                      .then(refresh)
                      .finally(() => setBusy(false));
                  }}
                  className={`rounded-[6px] px-6 py-3 text-sm font-semibold shadow-brand ${
                    row.unlocked
                      ? "border border-primary text-primary"
                      : "bg-[#693D94] text-primary-foreground"
                  }`}
                >
                  {row.unlocked ? "Lock again" : "Open for the leader"}
                </button>
              </div>
            </div>

            {openId === row.id && <ReportPanel cycleId={row.id} />}
          </div>
        ))}
      </div>
    </Section>
  );
}
